import { exportUsers, statusUpdate, userList } from 'api/user/user.js'
import BreadCrumb from 'components/common/BreadCrumb.js'
import { DataTable } from 'components/common/DataTable.js'
import ExportTable from 'components/common/ExportTable.js'
import Loader from 'components/common/Loader.js'
import NoDataFound from 'components/common/NoDataFound.js'
import SearchInput from 'components/common/SearchInput.js'
import { UserStatus } from 'constants/user.js'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { confirmAlert } from 'react-confirm-alert'
import { toast } from 'react-toastify'
import { Badge, Container, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap'

export default function User () {
  const [loading, setLoading] = useState()
  const [users, setUsers] = useState([])
  const [meta, setMeta] = useState({ total: 0, perPage: 10, currentPage: 1 });
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const toggleDropdown = (id) => {
    setDropdownOpen(prev => (prev === id ? null : id));
  };

  useEffect(() => {
    getUserList({
      page: meta.currentPage,
      perPage: meta.perPage,
      search,
      order: sortBy,
      direction: sortOrder
    });
  }, [meta.currentPage, meta.perPage, search, sortBy, sortOrder])

  const handlePageChange = (page) => {
    const updatedMeta = { ...meta, currentPage: page };
    setMeta(updatedMeta);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    const updatedMeta = { ...meta, currentPage: page, perPage: newPerPage };
    setMeta(updatedMeta);
  };

  const handleSearch = (value) => {
    setSearch(value);
    const updatedMeta = { ...meta, currentPage: 1 };
    setMeta(updatedMeta);
  };

  const handleSort = (column, direction) => {
    const field = column || 'name';

    // setMeta((prev) => ({ ...prev, currentPage: prev.currentPage }));
    setSortBy(field);
    setSortOrder(direction);
  };

  const userStatusUpdate = (row) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="custom-ui"
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              border: '2px solid #dc3545'
            }}>

            <h4>Are you sure?</h4>
            <p>{`You are about to ${row.status === UserStatus.ACTIVE ? 'deactivate' : 'activate'} this user`}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={onClose}
                style={{
                  backgroundColor: '#ccc',
                  color: '#000',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
              Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const payload = {
                      user_id: row.id,
                      status: row.status === UserStatus.ACTIVE ? UserStatus.INACTIVE : UserStatus.ACTIVE
                    }
                    const res = await statusUpdate(payload);
                    getUserList({
                      page: meta.currentPage,
                      perPage: meta.perPage
                    })
                    toast.success(res.message);
                  } catch (error) {
                    toast.error(error?.response?.data?.message);
                  }
                  onClose();
                }}
                style={{
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {`Yes, ${row.status === UserStatus.ACTIVE ? 'Deactivate' : 'Activate'}`}
              </button>
            </div>
          </div>
        );
      }
    });
  };

  async function getUserList ({ page = 1, perPage = 10, search = '', order = '', direction = '' }) {
    try{
      setLoading(true)
      const params = new URLSearchParams()

      params.append('page', page);
      params.append('perPage', perPage);
      if (search?.trim()) {
        params.append('search', search.trim());
      }
      if(order) {
        params.append('order', order);
      }
      if(direction) {
        params.append('direction', direction)
      }
      const res = await userList(`?${params.toString()}`)

      const resultData = res.data.data.map((item) => {
        return {
          id: item?.id,
          name: item?.name,
          email: item?.email,
          address: item?.address,
          date_of_birth: item?.date_of_birth,
          user_type: item?.user_type,
          status: item?.status,
          order: item?.order,
          created_at: item?.created_at
        }
      })
      setUsers(resultData)
      setMeta({
        total: res.data.meta.total,
        perPage: res.data.meta.perPage,
        currentPage: res.data.meta.currentPage
      });
    }catch(error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch user list');
    }finally{
      setLoading(false)
    }
  }

  const handleExport = async (type) => {
    try {
      const response = await exportUsers(type);

      const contentType = response.headers['content-type'];

      const blob = new Blob([response.data], { type: contentType });

      const disposition = response.headers['content-disposition'];
      let filename = `users.${type}`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('File Downloaded Successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to export user list');
    }
  };

  const columns = [
    {
      name: 'Name',
      sortField: 'name',
      selector: row => row.name,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.name}</span>
        </div>
      ),
      sortable: true
    },
    {
      name: 'Email',
      sortField: 'email',
      selector: row => row.email,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.email}</span>
        </div>
      ),
      sortable: true
    },
    {
      name: 'Address',
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.address}</span>
        </div>
      )
    },
    {
      name: 'Date Of Birth',
      sortField: 'date_of_birth',
      selector: row => row.date_of_birth,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {row.date_of_birth ? <span>{dayjs(row.date_of_birth).format('YYYY-MM-DD')}</span> :
            <span></span>
          }
        </div>
      ),
      sortable: true
    },
    {
      name: 'Order Ids',
      selector: row => row.order,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{Array.isArray(row.order) && row.order.length > 0 ? row.order.join(', ') : ''}</span>
        </div>
      )
    },
    {
      name: 'Account Created(Date & time)',
      selector: row => row.created_at,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{dayjs(row.created_at).format('YYYY-MM-DD hh:mm A')}</span>
        </div>
      )
    },
    {
      name: 'Status',
      cell: row => row.status === UserStatus.ACTIVE ? (
        <span>
          <Badge color="success" pill>{row.status}</Badge>
        </span>
      ) : (
        <span>
          <Badge color="danger" pill>{row.status}</Badge>
        </span>
      ),
      sortable: false
    },
    {
      name: 'Actions',
      cell: row => (
        <Dropdown isOpen={dropdownOpen === row.id} toggle={() => toggleDropdown(row.id)} direction="down">
          <DropdownToggle tag="span" data-toggle="dropdown" aria-expanded={dropdownOpen}>
            <i className="ri-more-2-fill" style={{ fontSize: '20px', cursor: 'pointer' }}></i>
          </DropdownToggle>
          <DropdownMenu container="body"
            end
            style={{
              maxHeight: '150px',
              overflowY: 'auto',
              zIndex: 9999
            }}>
            <DropdownItem onClick={() => userStatusUpdate(row)}>
              <i className={row.status === UserStatus.ACTIVE ? 'ri-toggle-line me-2' : 'ri-toggle-fill me-2'} />
              {row.status === UserStatus.ACTIVE ? UserStatus.INACTIVE : UserStatus.ACTIVE}
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      )
    }
  ];

  document.title = 'User'

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center ongoingProjectsPg">
          <div className="mainPageHeader d-flex justify-content-between">
            <BreadCrumb title="User List" />
          </div>
          <div className="d-flex justify-content-end ongoingSearchField">
            <div className="d-flex align-items-center me-3" style={{ gap: '10px' }}>
              <SearchInput
                onSearch={handleSearch}
                className="w-100"
                inputStyle={{ width: '250px' }}
              />
              <ExportTable handleExport={handleExport}/>
            </div>
          </div>
        </div>
        <div className="accordionTable my-4" style={{
          width: "100%",
          overflow: 'visible',
          position: 'relative',
          zIndex: 1
        }}>
          {
            users.length > 0 && (
              <DataTable
                columns={columns}
                data={users}
                pagination
                paginationServer
                onSort={(column, sortDirection) => {
                  handleSort(column.sortField, sortDirection);
                }}
                sortServer
                paginationTotalRows={meta.total}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
              />
            )
          }
          {users.length === 0 && loading && <Loader/>}
          {users.length === 0 && !loading && (
            <NoDataFound message="No users available" />
          )}
        </div>
      </Container>
    </div>
  )
}
